// TikTok Pixel + Events API integration for Amorette
// Pixel ID: D7BJ22RC77UC584IPT90

const TIKTOK_PIXEL_ID = 'D7BJ22RC77UC584IPT90';

/**
 * Install TikTok Pixel base code (runs once on app init).
 * Standard ttq.load() pattern from TikTok's official docs.
 */
export const initTikTokPixel = () => {
  if (typeof window === 'undefined') return;
  if ((window as any).ttq) return; // Already loaded

  // TikTok Pixel base code
  (function (w: any, d: Document, t: string) {
    w.TiktokAnalyticsObject = t;
    const ttq = (w[t] = w[t] || []);
    ttq.methods = [
      'page', 'track', 'identify', 'instances', 'debug', 'on', 'off',
      'once', 'ready', 'alias', 'group', 'enableCookie', 'disableCookie',
      'holdConsent', 'revokeConsent', 'grantConsent',
    ];
    ttq.setAndDefer = function (t: any, e: string) {
      t[e] = function () {
        t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
      };
    };
    for (let i = 0; i < ttq.methods.length; i++) {
      ttq.setAndDefer(ttq, ttq.methods[i]);
    }
    ttq.instance = function (t: string) {
      const e = ttq._i[t] || [];
      for (let n = 0; n < ttq.methods.length; n++) {
        ttq.setAndDefer(e, ttq.methods[n]);
      }
      return e;
    };
    ttq.load = function (e: string, n?: any) {
      const r = 'https://analytics.tiktok.com/i18n/pixel/events.js';
      ttq._i = ttq._i || {};
      ttq._i[e] = [];
      ttq._i[e]._u = r;
      ttq._t = ttq._t || {};
      ttq._t[e] = +new Date();
      ttq._o = ttq._o || {};
      ttq._o[e] = n || {};
      const o = d.createElement('script');
      o.type = 'text/javascript';
      o.async = true;
      o.src = r + '?sdkid=' + e + '&lib=' + t;
      const a = d.getElementsByTagName('script')[0];
      a?.parentNode?.insertBefore(o, a);
    };

    ttq.load(TIKTOK_PIXEL_ID);
    ttq.page();
  })(window, document, 'ttq');
};

/**
 * Generate a unique event_id for deduplication between Pixel + Events API.
 */
const generateEventId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
};

type TikTokEventName =
  | 'PageView'
  | 'ViewContent'
  | 'Lead'
  | 'CompleteRegistration'
  | 'AddPaymentInfo'
  | 'CompletePayment';

interface TikTokEventParams {
  content_type?: string;
  content_id?: string;
  content_name?: string;
  value?: number;
  currency?: string;
  description?: string;
  [key: string]: unknown;
}

/**
 * Fire a TikTok event via both Pixel (client) and Events API (server).
 * Uses shared event_id for deduplication.
 */
export const trackTikTokEvent = (
  eventName: TikTokEventName,
  params?: TikTokEventParams,
  options?: { serverOnly?: boolean }
) => {
  const event_id = generateEventId();

  // 1. Client-side Pixel fire (unless server-only)
  if (!options?.serverOnly && typeof window !== 'undefined' && (window as any).ttq) {
    try {
      (window as any).ttq.track(eventName, { ...params, event_id });
    } catch (e) {
      console.warn('[TikTok Pixel] Client track failed:', e);
    }
  }

  // 2. Server-side Events API (non-blocking)
  fireServerEvent(eventName, event_id, params).catch((err) =>
    console.warn('[TikTok Events API] Server track failed:', err)
  );
};

/**
 * Send event to our edge function which forwards to TikTok Events API.
 */
async function fireServerEvent(
  eventName: TikTokEventName,
  event_id: string,
  params?: TikTokEventParams
) {
  const projectId = import.meta.env["VITE_SUPABASE_PROJECT_ID"];
  if (!projectId) return;

  const url = `https://${projectId}.supabase.co/functions/v1/tiktok-events-api`;

  await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      event: eventName,
      event_id,
      url: window.location.href,
      referrer: document.referrer || undefined,
      user_agent: navigator.userAgent,
      ...params,
    }),
    keepalive: true, // Ensure it fires even on page unload
  });
}

/**
 * Track TikTok PageView — call on route changes.
 */
export const trackTikTokPageView = () => {
  if (typeof window === 'undefined' || !(window as any).ttq) return;
  try {
    (window as any).ttq.page();
  } catch (e) {
    console.warn('[TikTok Pixel] PageView failed:', e);
  }
};
